import { ChevronLeft, ChevronRight } from 'lucide-react';
import { useCallback, useEffect, useRef, useState } from 'react';
import { cn } from '@/lib/cn';
import type { MediaCard } from '@/protocol/media';
import { MediaTile } from './MediaTile';

export function MediaRow({
  cards,
  onSelect,
}: {
  cards: MediaCard[];
  onSelect: (card: MediaCard) => void;
}) {
  const track = useRef<HTMLDivElement>(null);
  const [overflow, setOverflow] = useState({ left: false, right: false });

  const measure = useCallback(() => {
    const node = track.current;
    if (!node) return;
    setOverflow({
      left: node.scrollLeft > 8,
      right: node.scrollLeft + node.clientWidth < node.scrollWidth - 8,
    });
  }, []);

  useEffect(() => {
    measure();
    const node = track.current;
    if (!node) return;
    // Arrows depend on the track's own width, which changes with the drawer as well as the window.
    const observer = new ResizeObserver(measure);
    observer.observe(node);
    return () => observer.disconnect();
  }, [measure, cards]);

  function nudge(direction: -1 | 1) {
    const node = track.current;
    if (!node) return;
    node.scrollBy({ left: direction * node.clientWidth * 0.8, behavior: 'smooth' });
  }

  return (
    <div className="group/row relative">
      <div
        ref={track}
        onScroll={measure}
        className="no-scrollbar flex snap-x gap-3 overflow-x-auto scroll-smooth pb-1"
      >
        {cards.map((card) => (
          <MediaTile key={card.id} card={card} onSelect={onSelect} />
        ))}
      </div>

      <Arrow side="left" show={overflow.left} onClick={() => nudge(-1)} />
      <Arrow side="right" show={overflow.right} onClick={() => nudge(1)} />
    </div>
  );
}

function Arrow({
  side,
  show,
  onClick,
}: {
  side: 'left' | 'right';
  show: boolean;
  onClick: () => void;
}) {
  if (!show) return null;
  return (
    <button
      type="button"
      aria-label={side === 'left' ? 'Scroll left' : 'Scroll right'}
      onClick={onClick}
      className={cn(
        'border-line bg-raised/90 text-muted hover:text-text absolute top-[28%] grid size-9 place-items-center rounded-full border opacity-0 shadow-xl backdrop-blur transition',
        'group-hover/row:opacity-100 focus-visible:opacity-100',
        side === 'left' ? '-left-3' : '-right-3',
      )}
    >
      {side === 'left' ? <ChevronLeft className="size-4" /> : <ChevronRight className="size-4" />}
    </button>
  );
}
